import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiGatewayService {
  private readonly baseUrl = environment.appConfig.apiUrl;
  private readonly http = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);

  get<T>(url: string, version: string, params: Record<string, unknown>): Observable<T> {
    return this.request<T>('GET', url, version, params, null);
  }

  post<T>(url: string, version: string, body: Record<string, unknown>): Observable<T> {
    return this.request<T>('POST', url, version, null, body);
  }

  put<T>(url: string, version: string, body: Record<string, unknown>): Observable<T> {
    return this.request<T>('PUT', url, version, null, body);
  }

  patch<T>(url: string, version: string, body: Record<string, unknown>): Observable<T> {
    return this.request<T>('PATCH', url, version, null, body);
  }

  delete<T>(url: string, version: string, params: Record<string, unknown>): Observable<T> {
    return this.request<T>('DELETE', url, version, params, null);
  }

  private request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    version: string,
    params: Record<string, unknown> | null,
    body: Record<string, unknown> | null,
  ): Observable<T> {
    return from(Promise.resolve(this.localStorageService.getUser())).pipe(
      switchMap((user) => {
        let headers = new HttpHeaders();
        if (body !== null && body !== undefined) {
          headers = headers.append('Content-Type', 'application/json');
        }
        if (user?.['oss-api-token']) {
          headers = headers.append('oss-api-token', user['oss-api-token']);
        }
        let _params = Object.entries(params ?? {}).reduce((previous, current) => {
          const [key, value] = current;
          if (value !== null && value !== undefined) {
            previous = previous.append(key, value.toString());
          }
          return previous;
        }, new HttpParams());
        if (method === 'GET') {
          _params = _params.append('_ts', Date.now().toString());
        }
        return this.http.request<T>(
          method,
          `${this.baseUrl}/${url}${version ? `/${version}` : ''}`,
          { headers, params: _params, body },
        );
      }),
    );
  }
}
